import React from "react";
//#region redux
import { useSelector } from "react-redux";
import { AppState } from "store";
//#endregion
//#region types
import { CustomerInformationLoaderProps } from "models/propTypes/invoicePropTypes";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
//#endregion
//#region components
import { useCustomerService } from "serviceHooks";
import useLoadSendMailOptions from "../sendMailOptions/useSendMailOptions";
import useGetMailOptionDefaults from "../sendMailOptions/useGetMailOptionDefaults";
import { useGetEmailValues } from "./useGetEmailValues";
import useInvoiceDefaultDeliverySettings from "../settings/useInvoiceDefaultDeliverySettings";
import useSetMailOptionsDefaults from "../sendMailOptions/useSetMailOptionsDefaults";
//#endregion
import { InvoiceHelper, LocalizationHelperInstance, PersonNumberHelper } from "utils/helpers";
import { useFloatingMessages } from "components/common";
import { TranslationKeys } from "translations";
import { StyleEnums } from "models/enums";

const CustomerInformationLoader: React.FC<CustomerInformationLoaderProps> = (props) => {
  const customerService = useCustomerService();
  const { values, invoiceId, setValues, lastSavedSendProperties, connect, revenueAccountsEnabled, creditedInvoiceId } =
    props;
  const noVatCompany = useSelector((store: AppState) => store.auth.selectedCompany.noVat);
  const { enqueueMessage } = useFloatingMessages();

  //#region load customer information on a intial load
  React.useEffect(() => {
    if (values.customer?.id) {
      loadCustomerInformation(values.customer.id as number, values.referencePerson?.id as number);
    }
  }, [invoiceId]);

  const { loadSendMailOptions } = useLoadSendMailOptions();
  const { getCreateInvoiceDefaultEmailOptions } = useGetMailOptionDefaults();
  const { getUpdatedDefaultEmailOptions } = useSetMailOptionsDefaults(connect, values);
  const { getDefaultDeliverySettings } = useInvoiceDefaultDeliverySettings();
  const getEmailValues = useGetEmailValues();

  const loadCustomerInformation = (customerId: number, referencePersonId?: number) => {
    Promise.all([customerService.getCustomer(customerId), loadSendMailOptions(invoiceId, customerId)]).then(
      ([customerResult, sendMailOptions]) => {
        if (customerResult) {
          if (customerId === values.customer?.id) {
            let referenceEmail: string | undefined;

            if (referencePersonId !== undefined) {
              referenceEmail = customerResult.referencePersonsList?.find(
                (refPerson) => refPerson.id === referencePersonId
              )?.emailAddress;
            }

            const customerPersonNumber: string | undefined =
              customerResult.orgNumber &&
              PersonNumberHelper.normalize(customerResult.orgNumber, true)?.replace("-", "");
            const tenantsOrgNumber: string | undefined =
              customerResult.tenantsOwnerAssociationOrgNumber &&
              PersonNumberHelper.normalize(customerResult.tenantsOwnerAssociationOrgNumber, true)?.replace("-", "");

            const { mailOptions, mailOptionsAuto, defaultEmailToSettings, defaultReminderEmailToSettings } =
              getEmailValues(customerResult, referenceEmail, sendMailOptions);

            setValues((prevState) => {
              const newValues = {
                ...prevState,
                customerEmail: customerResult.emailAddress,
                // Hydrates the customer half of the row VAT lock for an opened invoice. The rows'
                // half (articleAccount/articleVat/articleIsAgentSale) comes with the invoice
                // itself, see useInvoiceInitialValues. No extra request: this customer load already
                // happened on every open before the lock existed.
                isDomesticCustomer: InvoiceHelper.isDomesticCountryCode(customerResult.billingAddress?.countryCode),
                referencePersonEmail: referenceEmail,
                rutRot: {
                  ...prevState.rutRot,
                  rutRotPersonNumber: !prevState.rutRot?.houseWorkType
                    ? customerPersonNumber
                    : prevState.rutRot?.rutRotPersonNumber,
                  propertyDesignation: !prevState.rutRot?.houseWorkType
                    ? customerResult.propertyDesignation
                    : prevState.rutRot?.propertyDesignation,
                  tenantsOrgNumber: !prevState.rutRot?.houseWorkType
                    ? tenantsOrgNumber
                    : prevState.rutRot?.tenantsOrgNumber,
                  apartmentNumber: !prevState.rutRot?.houseWorkType
                    ? customerResult.apartmentNumber
                    : prevState.rutRot?.apartmentNumber,
                },
                otherSettings: {
                  ...prevState.otherSettings,
                  defaultReminderEmailToSettings: defaultReminderEmailToSettings,
                },
                recurringSettings: {
                  ...prevState.recurringSettings,
                  defaultEmailToSettings: defaultEmailToSettings,
                },
                mailOptions: mailOptions,
                previewSendMail: getCreateInvoiceDefaultEmailOptions(
                  mailOptions,
                  defaultEmailToSettings,
                  lastSavedSendProperties
                ),
                defaultSendType:
                  customerResult.invoiceDeliverySettings?.defaultInvoiceSendOption ?? prevState.defaultSendType,
                defaultEmailToSettings: defaultEmailToSettings,
              };

              const defaultSettings = getDefaultDeliverySettings(customerResult);

              const isRecurringSendMailAuto =
                newValues.recurringSettings.recurringInvoiceSendType ===
                  WintSuperkollAPIModel.RecurringInvoiceSendType.Auto &&
                newValues.recurringSettings.recurringInvoiceAutomaticSendType ===
                  WintSuperkollAPIModel.InvoiceSendType.SendByEmail;

              if (!isRecurringSendMailAuto) {
                newValues.recurringSettings.recurringSendMail = getUpdatedDefaultEmailOptions(
                  {
                    ...(newValues.recurringSettings.recurringSendMail ?? {}),
                    emailSubject: defaultSettings.invoiceTitleTemplate,
                    emailMessage: defaultSettings.invoiceMessageTemplate,
                  },
                  mailOptionsAuto,
                  defaultEmailToSettings
                );
              }

              const isReminderSendMail =
                newValues.otherSettings.automaticReminderSendType === WintSuperkollAPIModel.InvoiceSendMethod.Email;

              if (!isReminderSendMail) {
                newValues.otherSettings.automaticReminderSendMail = getUpdatedDefaultEmailOptions(
                  {
                    ...(newValues.otherSettings.automaticReminderSendMail ?? {}),
                    emailSubject: defaultSettings.invoiceReminderTitleTemplate,
                    emailMessage: defaultSettings.invoiceReminderMessageTemplate,
                  },
                  mailOptionsAuto,
                  defaultReminderEmailToSettings
                );
              }

              return newValues;
            });
          }
        }
      }
    );
  };
  //#endregion

  //#region snap drifted locked rows back to the article's VAT once the lock can be evaluated
  // An opened invoice may carry rows saved with a VAT that no longer matches the article's (edited
  // while the lock was inert, or before the feature). Locking them as-is would freeze a wrong
  // value the user can't correct, so they snap back with a notice - the same behaviour as a
  // customer change in customerSelectionStep. Two async signals gate the lock, the customer's
  // country (loaded above) and the company's opt-in flag (loaded by the container), and they land
  // in either order, so this waits for both rather than running from either callback. Acts at most
  // once per opened invoice; later customer changes are customerSelectionStep's job. The credit
  // exemption lives in getRowsNeedingVatReset itself, so both paths get it.
  const hydrationVatResetDone = React.useRef(false);
  React.useEffect(() => {
    hydrationVatResetDone.current = false;
  }, [invoiceId]);

  React.useEffect(() => {
    if (hydrationVatResetDone.current) return;
    if (values.isDomesticCustomer === undefined || revenueAccountsEnabled === undefined) return;

    // Decided before setValues so the updater stays pure (StrictMode re-runs updaters).
    const rowsNeedingVatReset = InvoiceHelper.getRowsNeedingVatReset(
      values.products,
      values.isDomesticCustomer,
      revenueAccountsEnabled,
      noVatCompany,
      creditedInvoiceId
    );
    if (rowsNeedingVatReset.size === 0) return;
    hydrationVatResetDone.current = true;

    setValues((prevState) => ({
      ...prevState,
      products: prevState.products?.map((p) =>
        // isRowVatLocked already guaranteed articleVat is defined for these rows.
        rowsNeedingVatReset.has(p.uniqueId) ? { ...p, tax: p.articleVat! } : p
      ),
    }));
    enqueueMessage(
      LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.VatResetOnOpenNotice),
      StyleEnums.DisplayType.Info,
      true
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.isDomesticCustomer, revenueAccountsEnabled, noVatCompany]);
  //#endregion

  return <></>;
};

export default CustomerInformationLoader;
